/*
  Add-on rule: a Firefly Lantern or a mattress cover can only be ordered together with a teepee.
  Every checkout entry point (GoKwik checkout in the cart page/drawer, GoKwik Buy Now, the Shopify
  checkout button and express buttons) is checked first: if the cart has a lamp but no teepee, the
  checkout is stopped and the shopper is told why (lamps and covers are told apart in the message). The same
  check also stops a "Customize Name" teepee that has no name and asks for it (see nameNotice). Teepee ids come from layout/theme.liquid
  (window.ttLampRule, built from the Candy and Enchanted collections). If the cart can't be read,
  checkout carries on as normal so a network hiccup never blocks a real order.
*/
(function () {
	const rule = window.ttLampRule || { tents: [], lampPage: false };
	const tents = new Set(rule.tents.map(String));
	const isLamp = (item) => (item.handle || '').indexOf('firefly-lantern') === 0;
	const isCover = (item) => (item.handle || '').indexOf('mattress-cover') === 0;
	const needsTent = (item) => isLamp(item) || isCover(item);

	const CHECKOUT =
		'.gokwik-checkout button, button[name="checkout"], input[name="checkout"], a[href$="/checkout"], .shopify-payment-button button, .additional-checkout-buttons button';
	const BUY_NOW = '#gokwik-buy-now';
	let bypass = false;

	function lampWithoutTent(cart) {
		const lamps = cart.items.filter(needsTent);
		const hasTent = cart.items.some((item) => tents.has(String(item.product_id)));
		return lamps.length > 0 && !hasTent ? lamps : null;
	}

	// Wording for the notice: "lantern", "mattress cover" or both.
	function label(items) {
		const lamp = !items || items.some(isLamp);
		const cover = items && items.some(isCover);
		if (lamp && cover) return { title: 'Lanterns and mattress covers come with a teepee', what: 'lantern and mattress cover', it: 'them' };
		if (cover) return { title: 'The mattress cover comes with a teepee', what: 'mattress cover', it: 'it' };
		return { title: 'The Firefly Lantern comes with a teepee', what: 'lantern', it: 'it' };
	}

	const style = document.createElement('style');
	style.textContent =
		'.tt-lamp-notice{position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(0,0,0,.55)}' +
		'.tt-lamp-notice__panel{position:relative;width:100%;max-width:420px;padding:28px 22px 22px;border-radius:14px;background:#fff;color:#1a1a1a;text-align:center}' +
		'.tt-lamp-notice__panel h2{margin:0 0 10px;font-size:19px;line-height:1.3}' +
		'.tt-lamp-notice__panel p{margin:0 0 18px;font-size:14px;line-height:1.6}' +
		'.tt-lamp-notice__actions{display:flex;flex-wrap:wrap;gap:10px;justify-content:center}' +
		'.tt-lamp-notice__actions .button{min-width:150px}' +
		'.tt-lamp-notice__close{position:absolute;top:6px;right:10px;border:0;background:none;font-size:26px;line-height:1;cursor:pointer;color:inherit}';
	document.head.appendChild(style);

	function close(box) {
		box.remove();
		document.body.style.overflow = '';
	}

	function notice(lamps) {
		const l = label(lamps);
		const box = document.createElement('div');
		box.className = 'tt-lamp-notice';
		box.setAttribute('role', 'dialog');
		box.setAttribute('aria-modal', 'true');
		box.setAttribute('aria-labelledby', 'tt-lamp-notice-title');
		box.innerHTML =
			'<div class="tt-lamp-notice__panel">' +
			'<h2 id="tt-lamp-notice-title">' + l.title + '</h2>' +
			'<p>Our ' + l.what + ' is an add-on and can only be ordered together with a teepee. Add a teepee to your cart' +
			(lamps ? ', or remove the ' + l.what + ' to continue.' : ' and add the ' + l.what + ' from the teepee page.') +
			'</p>' +
			'<div class="tt-lamp-notice__actions">' +
			'<a class="button button--primary" href="/collections/teepee-tents">Shop teepees</a>' +
			(lamps ? '<button type="button" class="button button--secondary" data-remove>Remove ' + l.what + '</button>' : '') +
			'</div>' +
			'<button type="button" class="tt-lamp-notice__close" aria-label="Close" data-close>&times;</button>' +
			'</div>';
		box.addEventListener('click', (event) => {
			if (event.target === box || event.target.closest('[data-close]')) close(box);
		});
		const remove = box.querySelector('[data-remove]');
		if (remove) {
			remove.addEventListener('click', async () => {
				remove.disabled = true;
				const updates = {};
				lamps.forEach((item) => (updates[item.key] = 0));
				await fetch(`${window.routes.cart_update_url}.js`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify({ updates }),
				}).catch(() => {});
				window.location.reload();
			});
		}
		document.addEventListener('keydown', function onKey(event) {
			if (event.key !== 'Escape') return;
			close(box);
			document.removeEventListener('keydown', onKey);
		});
		document.body.appendChild(box);
		document.body.style.overflow = 'hidden';
		box.querySelector('a').focus();
	}

	/*
	  Name personalisation (QA-01): a "Customize Name" teepee costs ₹1,200 more and is printed with the name,
	  so it must never reach checkout without one (e.g. added from a ?variant= link before the product page
	  check existed). Ask for the missing name here, save it on the cart line, then carry on to checkout.
	*/
	const NAME_KEY = 'Custom Name';
	const isNamed = (item) => /customi[sz]e name/i.test(item.variant_title || '');
	const nameless = (cart) =>
		cart.items
			.map((item, i) => ({ item, line: i + 1 }))
			.filter(({ item }) => isNamed(item) && !String((item.properties || {})[NAME_KEY] || '').trim());

	function nameNotice(missing, target) {
		const box = document.createElement('div');
		box.className = 'tt-lamp-notice';
		box.setAttribute('role', 'dialog');
		box.setAttribute('aria-modal', 'true');
		box.setAttribute('aria-labelledby', 'tt-name-notice-title');
		const fields = missing
			.map(
				({ item, line }) =>
					'<label style="display:block;text-align:left;margin:0 0 12px;font-size:14px">' +
					'Name to print on <strong>' + item.product_title.replace(/</g, '&lt;') + '</strong> (max 8 letters)' +
					'<input data-line="' + line + '" maxlength="8" required autocomplete="off" ' +
					'style="display:block;width:100%;margin-top:6px;padding:10px;font-size:16px;border:1px solid #ccc;border-radius:6px;text-transform:uppercase">' +
					'</label>'
			)
			.join('');
		box.innerHTML =
			'<div class="tt-lamp-notice__panel">' +
			'<h2 id="tt-name-notice-title">Which name should we print?</h2>' +
			'<p>You chose name personalisation (₹1,200) but did not add the name.</p>' +
			'<form>' + fields +
			'<p data-error style="display:none;color:#b42318;margin:0 0 12px">Please enter the name.</p>' +
			'<div class="tt-lamp-notice__actions"><button type="submit" class="button button--primary">Save and continue</button></div>' +
			'</form>' +
			'<button type="button" class="tt-lamp-notice__close" aria-label="Close" data-close>&times;</button>' +
			'</div>';
		box.addEventListener('click', (event) => {
			if (event.target === box || event.target.closest('[data-close]')) close(box);
		});
		box.querySelectorAll('input').forEach((input) => input.addEventListener('input', () => (input.value = input.value.toUpperCase())));
		box.querySelector('form').addEventListener('submit', async (event) => {
			event.preventDefault();
			const inputs = [...box.querySelectorAll('input')];
			if (inputs.some((input) => !input.value.trim())) {
				box.querySelector('[data-error]').style.display = 'block';
				return;
			}
			const button = box.querySelector('button[type="submit"]');
			button.disabled = true;
			try {
				// Same line order, so update one line at a time by its position.
				for (const input of inputs) {
					const { item, line } = missing.find((m) => String(m.line) === input.dataset.line);
					const res = await fetch(`${window.routes.cart_change_url}.js`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
						body: JSON.stringify({ line, quantity: item.quantity, properties: Object.assign({}, item.properties, { [NAME_KEY]: input.value.trim().toUpperCase() }) }),
					});
					if (!res.ok) throw new Error('cart change failed');
				}
				close(box);
				proceed(target);
			} catch (error) {
				button.disabled = false;
				box.querySelector('[data-error]').textContent = 'Could not save the name. Please try again.';
				box.querySelector('[data-error]').style.display = 'block';
			}
		});
		document.body.appendChild(box);
		document.body.style.overflow = 'hidden';
		box.querySelector('input').focus();
	}

	function proceed(target) {
		const gokwik = target.closest('.gokwik-checkout button');
		if (gokwik && typeof window.onCheckoutClick === 'function') return window.onCheckoutClick(gokwik);
		bypass = true;
		try {
			target.click();
		} finally {
			bypass = false;
		}
	}

	document.addEventListener(
		'click',
		(event) => {
			if (bypass) return;
			const buyNow = event.target.closest(BUY_NOW);
			if (buyNow) {
				// GoKwik Buy Now orders only the product on the page, so on a lamp page it is always a lamp alone.
				if (!rule.lampPage) return;
				event.preventDefault();
				event.stopImmediatePropagation();
				notice(null);
				return;
			}
			const target = event.target.closest(CHECKOUT);
			if (!target || target.disabled) return;
			event.preventDefault();
			event.stopImmediatePropagation();
			fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
				.then((r) => r.json())
				.then((cart) => {
					const lamps = lampWithoutTent(cart);
					const missing = nameless(cart);
					if (lamps) notice(lamps);
					else if (missing.length) nameNotice(missing, target);
					else proceed(target);
				})
				.catch(() => proceed(target));
		},
		true
	);
})();
